import { useNavigate } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { useApp } from '../store/AppContext'
import { useNotifications, type AppNotification } from '../lib/notifications'

const urgencyStyle = {
  urgent: 'border-[#f1d5d8] border-l-[4px] border-l-[#d86f78] bg-white',
  important: 'border-[#eadfce] border-l-[4px] border-l-[#c99a62] bg-white',
  normal: 'border-[#d8e2ec] border-l-[4px] border-l-[#8da8c1] bg-white',
}

const urgencyIconStyle = {
  urgent: 'bg-[#fff3f3] text-[#c9636b]',
  important: 'bg-[#fbf5ec] text-[#aa7f4d]',
  normal: 'bg-[#f1f5f9] text-[#6f8da8]',
}

function relativeTime(iso: string) {
  const time = new Date(iso).getTime()
  if (!Number.isFinite(time)) return ''
  const mins = Math.max(0, Math.round((Date.now() - time) / 60000))
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

function NotificationIcon({ item, large }: { item: AppNotification; large: boolean }) {
  const size = large ? 'h-7 w-7' : 'h-5 w-5'

  if (item.kind === 'task') {
    return <svg viewBox="0 0 24 24" className={size} fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="3" width="14" height="18" rx="2"/><path d="m8 9 1.5 1.5L12 8M13.5 10H17M8 15l1.5 1.5L12 14M13.5 16H17"/></svg>
  }
  if (item.kind === 'shift' || item.kind === 'cover') {
    return <svg viewBox="0 0 24 24" className={size} fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>
  }
  if (item.kind === 'tag') {
    return <svg viewBox="0 0 24 24" className={size} fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z"/><path d="M8 9h8M8 13h5"/></svg>
  }
  if (item.kind === 'routine') {
    return <svg viewBox="0 0 24 24" className={size} fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 7h12l-2.5-2.5M19 17H7l2.5 2.5"/><path d="M19 7a7 7 0 0 1 0 10M5 17A7 7 0 0 1 5 7"/></svg>
  }
  return <svg viewBox="0 0 24 24" className={size} fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3v4M12 17v4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M3 12h4M17 12h4M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/><circle cx="12" cy="12" r="3"/></svg>
}

function CheckIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2"><path d="m5 12 4 4L19 6"/></svg>
}

export function Notifications() {
  const navigate = useNavigate()
  const { currentUser, interfaceMode, toggleTaskStatus } = useApp()
  const { notifications, unreadCount, isUnread, markRead, markAllRead, snooze } = useNotifications()
  const participantAT = currentUser.role === 'participant' && (interfaceMode === 'simple' || interfaceMode === 'visual')

  const open = (item: AppNotification) => {
    markRead(item.id)
    navigate(item.path)
  }

  const complete = (item: AppNotification) => {
    if (item.kind !== 'task' || !item.sourceId) return
    toggleTaskStatus(item.sourceId)
    markRead(item.id)
  }

  return (
    <div className="min-h-screen bg-[#fffefe]">
      <TopBar title="Notifications" subtitle="Updates and reminders for you" />
      <div className={`px-4 py-4 ${participantAT ? 'space-y-4' : 'space-y-3'}`}>
        <div className="flex items-center justify-between gap-3 rounded-[20px] border border-[#d8e2ec] bg-white p-4 shadow-[0_5px_18px_rgba(73,91,110,.035)]">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#f1f5f9] text-[#738fa9]">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>
            </div>
            <div className="min-w-0">
              <p className={`${participantAT ? 'text-[18px]' : 'text-[16px]'} font-bold text-[#344453]`}>{unreadCount} unread</p>
              <p className={`${participantAT ? 'text-[15px]' : 'text-[13px]'} mt-0.5 font-medium leading-5 text-[#7c8997]`}>Your updates stay together in Marnixia.</p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="shrink-0 rounded-xl border border-[#d8e2ec] bg-[#f8fafc] px-3 py-2.5 text-[12px] font-bold text-[#6b8299]">
              Mark all read
            </button>
          )}
        </div>

        {notifications.length === 0 && (
          <div className="rounded-[24px] border border-[#d8e2ec] bg-white p-8 text-center shadow-[0_5px_18px_rgba(73,91,110,.03)]">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#edf7f3] text-[#5b8c82]"><CheckIcon className="h-6 w-6" /></div>
            <p className="mt-3 text-lg font-bold text-[#344453]">You're all caught up</p>
            <p className="mt-1 text-sm font-medium text-[#7c8997]">New reminders and updates will appear here.</p>
          </div>
        )}

        {notifications.map((item) => {
          const unread = isUnread(item.id)
          return (
            <article key={item.id} className={`rounded-[22px] border p-4 shadow-[0_5px_18px_rgba(73,91,110,.035)] ${urgencyStyle[item.urgency]} ${unread ? 'ring-1 ring-[#cddae6]' : ''}`}>
              <div className="flex items-start gap-3">
                <div className={`flex shrink-0 items-center justify-center rounded-[15px] ${participantAT ? 'h-14 w-14' : 'h-11 w-11'} ${urgencyIconStyle[item.urgency]}`} aria-hidden="true">
                  <NotificationIcon item={item} large={participantAT} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`${participantAT ? 'text-[19px]' : 'text-[16px]'} font-bold leading-tight text-[#344453]`}>{item.title}</p>
                      <p className={`mt-1.5 ${participantAT ? 'text-[16px] leading-6' : 'text-[14px] leading-5'} font-medium text-[#6f7d8a]`}>{item.message}</p>
                    </div>
                    {unread && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-[#829db7]" aria-label="Unread" />}
                  </div>
                  <p className="mt-2 text-[12px] font-medium text-[#9aa4af]">{relativeTime(item.createdAt)}</p>

                  <div className={`mt-4 grid gap-2 ${item.canComplete ? 'grid-cols-1 min-[390px]:grid-cols-3' : 'grid-cols-2'}`}>
                    <button onClick={() => open(item)} className={`${participantAT ? 'py-3 text-sm' : 'py-2.5 text-[13px]'} rounded-xl bg-[#7f9bb5] font-bold text-white shadow-[0_2px_8px_rgba(85,112,139,.12)]`}>
                      Open
                    </button>
                    {item.canComplete && (
                      <button onClick={() => complete(item)} className={`${participantAT ? 'py-3 text-sm' : 'py-2.5 text-[13px]'} flex items-center justify-center gap-1.5 rounded-xl border border-[#c9dfd9] bg-[#f1f8f5] font-bold text-[#5d887f]`}>
                        <CheckIcon className="h-4 w-4" /> Done
                      </button>
                    )}
                    <button onClick={() => snooze(item.id, 60)} className={`${participantAT ? 'py-3 text-sm' : 'py-2.5 text-[13px]'} rounded-xl border border-[#dce3ea] bg-white font-bold text-[#758291]`}>
                      Remind me later
                    </button>
                  </div>
                </div>
              </div>
            </article>
          )
        })}
      </div>
    </div>
  )
}

